import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Typography,
} from '@mui/material';

export const ALL_CRITERIA_QUEUE_ID = 'all';

export type QueueSection = 'live' | 'notLive';

export const QUEUE_SECTIONS: QueueSection[] = ['live', 'notLive'];

export const SECTION_LABELS: Record<QueueSection, string> = {
  live: 'Live',
  notLive: 'Not live',
};

export interface QueueNavItem {
  id: string;
  section: QueueSection;
  name: string;
  why: string;
  urgent?: boolean;
}

export interface MarkingMetricsQueueProps {
  queues: QueueNavItem[];
  selectedQueueId: string;
  counts: Record<string, number>;
  sectionCounts: Record<QueueSection, number>;
  totalCount: number;
  onSelectQueue: (id: string) => void;
  matchCounts?: Record<string, number>;
  totalMatchCount?: number;
  hasActiveFilter?: boolean;
  filterMode?: 'search' | 'unjudged' | 'both';
}

const QueueSectionHeader = ({ label, count }: { label: string; count: number }) => (
  <ListSubheader
    disableSticky
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      lineHeight: '36px',
      bgcolor: 'grey.100',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      fontSize: 11,
    }}
  >
    {label}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {count}
    </Typography>
  </ListSubheader>
);

const MarkingMetricsQueue = ({
  queues,
  selectedQueueId,
  counts,
  sectionCounts,
  totalCount,
  onSelectQueue,
  matchCounts,
  totalMatchCount,
  hasActiveFilter = false,
  filterMode,
}: MarkingMetricsQueueProps) => {
  const isAllCriteria = selectedQueueId === ALL_CRITERIA_QUEUE_ID;

  return (
    <Paper
      component="nav"
      variant="outlined"
      aria-label="Marking metrics queues"
      sx={{ overflowY: 'auto', minHeight: 0, height: '100%' }}
    >
      <List dense disablePadding>
        {QUEUE_SECTIONS.map(section => (
          <Box key={section}>
            <QueueSectionHeader label={SECTION_LABELS[section]} count={sectionCounts[section]} />
            {queues
              .filter(item => item.section === section)
              .map(item => {
                const matchCount = matchCounts ? (matchCounts[item.id] ?? 0) : 0;
                const hasMatches = matchCount > 0;
                const isSelected = item.id === selectedQueueId;
                const highlightBorder = hasActiveFilter && hasMatches;
                const dimNonMatching = hasActiveFilter && !hasMatches;

                return (
                  <ListItemButton
                    key={item.id}
                    selected={isSelected}
                    onClick={() => onSelectQueue(item.id)}
                    sx={{
                      py: 1,
                      alignItems: 'flex-start',
                      gap: 1,
                      borderLeft: highlightBorder ? '4px solid' : '4px solid transparent',
                      borderColor: highlightBorder
                        ? filterMode === 'unjudged'
                          ? 'warning.main'
                          : 'primary.main'
                        : 'transparent',
                      bgcolor:
                        highlightBorder && !isSelected
                          ? filterMode === 'unjudged'
                            ? 'rgba(237, 108, 2, 0.08)'
                            : 'rgba(25, 118, 210, 0.08)'
                          : undefined,
                      opacity: dimNonMatching ? 0.45 : 1,
                      transition: 'background-color 150ms, opacity 150ms, border-color 150ms',
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.8,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: hasActiveFilter
                          ? hasMatches
                            ? filterMode === 'unjudged'
                              ? 'warning.main'
                              : 'primary.main'
                            : 'grey.300'
                          : item.urgent && counts[item.id]
                            ? 'error.main'
                            : counts[item.id]
                              ? 'grey.400'
                              : 'grey.200',
                      }}
                    />
                    <ListItemText
                      primary={item.name}
                      secondary={item.why}
                      primaryTypographyProps={{
                        fontWeight: isSelected || highlightBorder ? 700 : 500,
                      }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip
                      size="small"
                      label={hasActiveFilter ? matchCount : (counts[item.id] ?? 0)}
                      color={
                        hasActiveFilter
                          ? hasMatches
                            ? filterMode === 'unjudged'
                              ? 'warning'
                              : 'primary'
                            : 'default'
                          : item.urgent && counts[item.id]
                            ? 'error'
                            : 'default'
                      }
                      variant={hasActiveFilter && !hasMatches ? 'outlined' : 'filled'}
                      sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}
                    />
                  </ListItemButton>
                );
              })}
          </Box>
        ))}
        <QueueSectionHeader
          label="Explore"
          count={hasActiveFilter ? (totalMatchCount ?? 0) : totalCount}
        />
        {(() => {
          const allHasMatches = (totalMatchCount ?? 0) > 0;
          const highlightBorder = hasActiveFilter && allHasMatches;
          const dimNonMatching = hasActiveFilter && !allHasMatches;

          return (
            <ListItemButton
              selected={isAllCriteria}
              onClick={() => onSelectQueue(ALL_CRITERIA_QUEUE_ID)}
              sx={{
                py: 1,
                alignItems: 'flex-start',
                gap: 1,
                borderLeft: highlightBorder ? '4px solid' : '4px solid transparent',
                borderColor: highlightBorder
                  ? filterMode === 'unjudged'
                    ? 'warning.main'
                    : 'primary.main'
                  : 'transparent',
                bgcolor:
                  highlightBorder && !isAllCriteria
                    ? filterMode === 'unjudged'
                      ? 'rgba(237, 108, 2, 0.08)'
                      : 'rgba(25, 118, 210, 0.08)'
                    : undefined,
                opacity: dimNonMatching ? 0.45 : 1,
                transition: 'background-color 150ms, opacity 150ms, border-color 150ms',
              }}
            >
              <TableChartOutlinedIcon
                fontSize="small"
                sx={{
                  mt: 0.4,
                  color: highlightBorder
                    ? filterMode === 'unjudged'
                      ? 'warning.main'
                      : 'primary.main'
                    : 'text.secondary',
                }}
              />
              <ListItemText
                primary="All criteria"
                secondary="Complete list of all criteria and statuses."
                primaryTypographyProps={{
                  fontWeight: isAllCriteria || highlightBorder ? 700 : 500,
                }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Chip
                size="small"
                label={hasActiveFilter ? (totalMatchCount ?? 0) : totalCount}
                color={
                  hasActiveFilter
                    ? allHasMatches
                      ? filterMode === 'unjudged'
                        ? 'warning'
                        : 'primary'
                      : 'default'
                    : 'default'
                }
                variant={hasActiveFilter && !allHasMatches ? 'outlined' : 'filled'}
                sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}
              />
            </ListItemButton>
          );
        })()}
      </List>
    </Paper>
  );
};

export default MarkingMetricsQueue;
